import { sql } from 'drizzle-orm';
import {
  pgTable,
  index,
  timestamp,
  date,
  varchar,
  text,
  integer,
  boolean,
  uuid,
  primaryKey
} from "drizzle-orm/pg-core";
import { claims } from './claims';
import { taskPriorityEnum, taskStatusEnum } from './enums';
import { organizations } from './organizations';
import { users } from './users';

// Tasks table
export const tasks = pgTable("tasks", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }), // Optional for general tasks
  assignedTo: varchar("assigned_to").references(() => users.id),
  title: varchar("title").notNull(),
  description: text("description"),
  status: taskStatusEnum("status").default("pending"),
  priority: taskPriorityEnum("priority").default("medium"),
  dueAt: timestamp("due_at"),
  startAt: date("start_at"),
  completedAt: timestamp("completed_at"),
  lastReminderSentAt: timestamp("last_reminder_sent_at"), // Track when last reminder was sent to prevent spam
  createdBy: varchar("created_by").references(() => users.id).notNull(),

  orgId: uuid("org_id").references(() => organizations.id, { onDelete: "cascade" }),

  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_tasks_claim_id").on(table.claimId),
  index("idx_tasks_assigned_to").on(table.assignedTo),
  index("idx_tasks_due_at").on(table.dueAt),
  index("idx_tasks_start_at").on(table.startAt),
]);


// Task Assignees junction table — multi-user assignment with per-assignee completion tracking
export const taskAssignees = pgTable("task_assignees", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  taskId: uuid("task_id").references(() => tasks.id, { onDelete: "cascade" }).notNull(),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  completedAt: timestamp("completed_at"), // null = not yet done by this assignee
  assignedAt: timestamp("assigned_at").defaultNow().notNull(),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_task_assignees_task_id").on(table.taskId),
  index("idx_task_assignees_user_id").on(table.userId),
]);


// Reminder Configuration table for automated follow-ups
export const reminderConfigurations = pgTable("reminder_configurations", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Configuration
  reminderType: varchar("reminder_type").notNull(), // 'work_completion', 'documentation_request', etc.
  isActive: boolean("is_active").default(true),
  frequencyDays: integer("frequency_days").default(45), // How often to send reminders
  maxReminders: integer("max_reminders").default(10), // Maximum number of reminders to send
  
  // Tracking
  lastSentAt: timestamp("last_sent_at"),
  nextScheduledAt: timestamp("next_scheduled_at"),
  totalSent: integer("total_sent").default(0),
  
  // Status
  workCompleted: boolean("work_completed").default(false),
  completionDate: timestamp("completion_date"),
  completionConfirmedBy: varchar("completion_confirmed_by"),
  
  createdAt: timestamp("created_at").defaultNow(),
  updatedAt: timestamp("updated_at").defaultNow(),
}, (table) => [
  index("idx_reminder_configs_claim_id").on(table.claimId),
  index("idx_reminder_configs_next_scheduled").on(table.nextScheduledAt),
  index("idx_reminder_configs_is_active").on(table.isActive),
]);


// Reminder History table to track sent reminders
export const reminderHistory = pgTable("reminder_history", {
  id: uuid("id").primaryKey().default(sql`gen_random_uuid()`),
  configurationId: uuid("configuration_id").references(() => reminderConfigurations.id, { onDelete: "cascade" }).notNull(),
  claimId: uuid("claim_id").references(() => claims.id, { onDelete: "cascade" }).notNull(),
  
  // Email details
  recipientEmail: varchar("recipient_email").notNull(),
  recipientName: varchar("recipient_name"),
  emailSubject: text("email_subject").notNull(),
  emailBody: text("email_body").notNull(),
  
  // Status
  sentAt: timestamp("sent_at").defaultNow(),
  deliveryStatus: varchar("delivery_status").default("sent"), // sent, delivered, failed, bounced
  openedAt: timestamp("opened_at"),
  respondedAt: timestamp("responded_at"),
  
  // Response tracking
  responseType: varchar("response_type"), // 'work_complete', 'need_more_time', etc.
  responseNotes: text("response_notes"),
  
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => [
  index("idx_reminder_history_config_id").on(table.configurationId),
  index("idx_reminder_history_claim_id").on(table.claimId),
  index("idx_reminder_history_sent_at").on(table.sentAt),
]);
